import {
  Box,
  Card,
  Dialog,
  Grid,
  Stack,
  Typography,
  Alert,
  Chip,
  LinearProgress,
  Button,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { ReactElement, useState, useEffect, useCallback } from 'react';
import { useTranslate, FunctionField, useNotify, useRefresh } from 'react-admin';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { Resources } from '../../resource';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import ErrorPage403 from '../../../../error/403';
import { PermissionButton } from '../../../../../routes/components/permission-button';
import {
  getDailyPrizePoolSummary,
  DailyPrizePoolSummary,
  DailyPrizePoolConfig,
  PoolType,
} from '../../../services/redpacketService';
import EditDailyPrizePoolConfig from './Edit';
import CreateDailyPrizePoolConfig from './Create';
import { fNumber } from '../../../../../utils/format-number';
import moment from 'moment/moment';

const DailyPrizePoolList = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [popup, setPopup] = useState<ReactElement>();
  const [summary, setSummary] = useState<DailyPrizePoolSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const refresh = useRefresh();
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getDailyPrizePoolSummary();
      console.log('xxx result=== ', result);
      setSummary(result);
    } catch (error: any) {
      notify(error.message || translate('module.redpacket.failedToGetData'), { type: 'error' });
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [notify,translate]);

  useEffect(() => {
    fetchData();
    refresh();
  }, [fetchData, refresh]);

  const StatCard = ({
    title,
    config,
    status,
    poolType,
  }: {
    title: string;
    config?: DailyPrizePoolConfig;
    status?: any;
    poolType: PoolType;
  }) => {
    const consumptionRate = status?.consumptionRate;
    return (
      <Card sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">{title}</Typography>
          <Stack direction="row" spacing={1}>
            {config ? (
              <PermissionButton resource="redpacket" action="update">
                <IconButton
                  color="primary"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPopup(
                      <EditDailyPrizePoolConfig
                        poolType={poolType}
                        config={config}
                        onClose={() => {
                          setPopup(undefined);
                          fetchData();
                        }}
                      />
                    );
                  }}
                >
                  <EditIcon />
                </IconButton>
              </PermissionButton>
            ) : (
              <PermissionButton resource="redpacket" action="create">
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={(e) => {
                    e.preventDefault();
                    setPopup(
                      <CreateDailyPrizePoolConfig
                        poolType={poolType}
                        onClose={() => {
                          setPopup(undefined);
                          fetchData();
                        }}
                      />
                    );
                  }}
                >
                  {translate('actions.create')}
                </Button>
              </PermissionButton>
            )}
          </Stack>
        </Stack>

        {config ? (
          <>
            <Grid container spacing={2} mb={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  {translate('module.redpacket.dailyBudget')}
                </Typography>
                <Typography variant="h6">
                  ${fNumber(parseFloat(config.dailyBudget || '0'))}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  {translate('module.redpacket.resetHourUtc')}
                </Typography>
                <Typography variant="h6">{config.resetHourUTC}:00</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  {translate('module.redpacket.status')}
                </Typography>
                <Chip
                  label={config.isActive ? translate('resource.active') : translate('resource.inactive')}
                  color={config.isActive ? 'success' : 'error'}
                  size="small"
                />
              </Grid>
              {config.description && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    {translate('module.redpacket.description')}
                  </Typography>
                  <Typography variant="body2">{config.description}</Typography>
                </Grid>
              )}
            </Grid>

            {status && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {translate('module.redpacket.todayStatus')} ({moment(status.date).format('MM-DD')})
                </Typography>
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      {translate('module.redpacket.totalBudgetCol')}
                    </Typography>
                    <Typography variant="body1">
                      ${fNumber(parseFloat(status.totalBudget || '0'))}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      {translate('module.redpacket.remainingAmount')}
                    </Typography>
                    <Typography variant="body1">
                      ${fNumber(parseFloat(status.remainingAmount || '0'))}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      {translate('module.redpacket.spentAmount')}
                    </Typography>
                    <Typography variant="body1">
                      ${fNumber(parseFloat(status.spentAmount || '0'))}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      {translate('module.redpacket.reservedAmount')}
                    </Typography>
                    <Typography variant="body1">
                      ${fNumber(parseFloat(status.reservedAmount || '0'))}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      {translate('module.redpacket.packetsIssuedCol')}
                    </Typography>
                    <Typography variant="body1">{status.packetsIssued || 0}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      {translate('module.redpacket.packetsClaimedCol')}
                    </Typography>
                    <Typography variant="body1">{status.packetsClaimed || 0}</Typography>
                  </Grid>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {translate('module.redpacket.isAvailable')}
                  </Typography>
                  <Chip
                    label={status.isAvailable ? translate('module.redpacket.yes') : translate('module.redpacket.no')}
                    color={status.isAvailable ? 'success' : 'error'}
                    size="small"
                  />
                </Grid>
                <Box sx={{ mt: 2 }}>
                  <Stack direction="row" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      {translate('module.redpacket.consumptionRateLabel')}
                    </Typography>
                    <Typography variant="body2">{consumptionRate.toFixed(2)}%</Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={consumptionRate} />
                </Box>
              </>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {translate('module.redpacket.noConfigurationFound')}
          </Typography>
        )}
      </Card>
    );
  };

  return (
    <PermissionGuard resource="redpacket" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.redpacket.dailyPrizePoolConfig')}
          links={[
            { name: translate('module.redpacket.redPacketManagement'), href: `/${Resources.redpacket}` },
            { name: translate('module.redpacket.dailyPrizePoolConfig') },
          ]}
        />

        {summary?.isAnyPoolDepleted && summary.depletionMessage && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            {summary.depletionMessage}
          </Alert>
        )}

        <Box sx={{ mt: 3 }}>
          <StatCard
            title={translate('module.redpacket.sharedPoolName')}
            config={summary?.sharedPoolConfig}
            status={summary?.sharedPoolStatus}
            poolType={PoolType.SHARED}
          />
          <StatCard
            title={translate('module.redpacket.criticalHitPoolName')}
            config={summary?.criticalHitPoolConfig}
            status={summary?.criticalHitPoolStatus}
            poolType={PoolType.CRITICAL_HIT}
          />
          <StatCard
            title={translate('module.redpacket.criticalHitDesignatedPoolName')}
            config={summary?.criticalHitDesignatedPoolConfig}
            status={summary?.criticalHitDesignatedPoolStatus}
            poolType={PoolType.CRITICAL_HIT_DESIGNATED}
          />
        </Box>

        {popup && (
          <Dialog open maxWidth="md" fullWidth>
            {popup}
          </Dialog>
        )}
      </DashboardContent>
    </PermissionGuard>
  );
};

export default DailyPrizePoolList;
